/**
 * Shared file-type validation for upload inputs.
 *
 * Each preset exports:
 *  - the canonical extension list (lowercase, no leading dot)
 *  - an `accept` attribute string for `<input type="file">` browser-level filtering
 *  - a human-readable label for hints / error toast bodies
 *  - MIME types for validation
 *
 * Use `partitionFilesByAllowed` to split a `FileList` into accepted and
 * rejected files based on extension, MIME type, and file signature.
 *
 * SECURITY NOTE: The HTML accept attribute is only a UX hint on Windows.
 * Users can bypass it. Always validate server-side.
 */

export type FileTypePreset = {
  extensions: readonly string[];
  mimeTypes: readonly string[];
  accept: string;
  label: string;
};

const buildAccept = (exts: readonly string[]) => exts.map((e) => `.${e}`).join(',');
const buildLabel = (exts: readonly string[]) => exts.map((e) => e.toUpperCase()).join(', ');

const buildPreset = (
  extensions: readonly string[],
  mimeTypes: readonly string[],
): FileTypePreset => ({
  extensions,
  mimeTypes,
  accept: buildAccept(extensions),
  label: buildLabel(extensions),
});

/**
 * Documents: PDFs, Word docs, spreadsheets, CSV/text, and images.
 * Used for job document uploads (WRK-5564).
 */
export const DOCUMENT_FILE_TYPES = buildPreset(
  ['csv', 'doc', 'docx', 'pdf', 'txt', 'xls', 'xlsx', 'jpeg', 'jpg', 'png'] as const,
  [
    'text/csv',
    'application/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
  ] as const,
);

/** Documents + spreadsheets. Used for reject / request-info / bulk action attachments. */
export const ATTACHMENT_FILE_TYPES = buildPreset(
  ['csv', 'doc', 'docx', 'pdf', 'txt', 'xls', 'xlsx', 'jpeg', 'jpg', 'png'] as const,
  [
    'text/csv',
    'application/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
  ] as const,
);

/** Photo-only uploads (data capture additional images). */
export const IMAGE_FILE_TYPES = buildPreset(
  ['jpg', 'jpeg', 'png'] as const,
  ['image/jpeg', 'image/png'] as const,
);

/** Lower-cased extension without the leading dot, or '' if the name has no extension. */
export function getFileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Extension → mimetype for every type the attachment/document presets allow.
 *
 * CWL-115 (R001-F006): some browsers/OSes report an empty `File.type` for
 * legitimate documents (notably .doc/.docx/.csv/.xls on Windows — see
 * isAllowedFile below). The picker accepts those on the extension alone, but the
 * presign endpoint validates the MIME type against
 * FILE_TYPE_PRESETS.ATTACHMENT.mimeTypes, which does NOT contain
 * 'application/octet-stream' — so the upload was refused with
 * 400 INVALID_FILE_TYPE (apps/backend/src/utils/fileValidation.ts:318-324).
 *
 * Values are exactly the members of ATTACHMENT_FILE_TYPES.mimeTypes; the test
 * `resolveUploadMimetype > every allowed extension maps into the preset` pins
 * that agreement. Mirrors ignite-core's
 * app/core-web/src/domains/work-orders/lib/resolveDocumentMimetype.ts.
 */
const EXTENSION_MIMETYPES = new Map<string, string>([
  ['csv', 'text/csv'],
  ['doc', 'application/msword'],
  ['docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ['pdf', 'application/pdf'],
  ['txt', 'text/plain'],
  ['xls', 'application/vnd.ms-excel'],
  ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ['jpeg', 'image/jpeg'],
  ['jpg', 'image/jpeg'],
  ['png', 'image/png'],
]);

/**
 * The Content-Type to send when presigning `fileName`. Prefers the browser's own
 * value, falls back to the extension, and only then to the generic binary type
 * (which the server rejects — by design, see EXTENSION_MIMETYPES above).
 *
 * The returned value is pinned into the presigned POST policy by
 * createDocumentUploadUrl (jobDocumentsService.ts:103-110), so it also becomes
 * the object's stored Content-Type and the job_file row's mime_type.
 */
export function resolveUploadMimetype(fileName: string, browserType: string): string {
  if (browserType.trim() !== '') return browserType;
  return EXTENSION_MIMETYPES.get(getFileExtension(fileName)) ?? 'application/octet-stream';
}

/**
 * Validate file against preset - extension is the gate; MIME tightens when present.
 *
 * Some browsers / OSes return an empty `file.type` for legitimate documents
 * (.docx, .doc), which previously caused valid files to be wrongly rejected.
 * Server-side signature validation (validateFileUpload middleware) remains the
 * security guarantee — see apps/backend/CLAUDE.md.
 */
export function isAllowedFile(file: File, preset: FileTypePreset): boolean {
  const extension = getFileExtension(file.name);
  if (!preset.extensions.includes(extension)) return false;

  // Normalize MIME type (remove charset if present)
  const normalizedMimeType = file.type.toLowerCase().split(';')[0].trim();

  // Empty MIME → trust the extension. Non-empty MIME → must match the preset.
  return normalizedMimeType === '' || preset.mimeTypes.includes(normalizedMimeType);
}

export interface PartitionedFiles {
  accepted: File[];
  rejected: File[];
}

export function partitionFilesByAllowed(
  files: File[] | FileList | null | undefined,
  preset: FileTypePreset,
): PartitionedFiles {
  const list = files ? Array.from(files) : [];
  const accepted: File[] = [];
  const rejected: File[] = [];
  for (const file of list) {
    // Reject dangerous files immediately
    if (isDangerousFile(file)) {
      rejected.push(file);
      continue;
    }
    // Validate against preset (extension + MIME type)
    (isAllowedFile(file, preset) ? accepted : rejected).push(file);
  }
  return { accepted, rejected };
}

/**
 * Dangerous file extensions that should never be allowed
 * Additional safety check beyond preset validation
 */
export const DANGEROUS_EXTENSIONS = [
  'exe',
  'bat',
  'cmd',
  'com',
  'pif',
  'scr',
  'vbs',
  'js',
  'jse',
  'wsf',
  'wsh',
  'ps1',
  'psm1',
  'msi',
  'dll',
  'app',
  'sh',
  'php',
  'asp',
  'aspx',
  'jsp',
] as const;

/**
 * Check if file has dangerous executable extension
 */
export function isDangerousFile(file: File): boolean {
  const ext = getFileExtension(file.name);
  return DANGEROUS_EXTENSIONS.includes(ext as any);
}

/**
 * Get detailed rejection reason for better error messages
 */
export function getFileRejectionReason(file: File, preset: FileTypePreset): string {
  if (isDangerousFile(file)) {
    return `${file.name}: Executable files are not permitted for security reasons`;
  }

  const extension = getFileExtension(file.name);
  if (!preset.extensions.includes(extension)) {
    return `${file.name}: File extension "${extension || 'none'}" not allowed`;
  }

  const normalizedMimeType = file.type.toLowerCase().split(';')[0].trim();
  if (normalizedMimeType !== '' && !preset.mimeTypes.includes(normalizedMimeType)) {
    return `${file.name}: File type "${file.type}" not allowed`;
  }

  return `${file.name}: File validation failed`;
}

/** Builds the standard "File type not supported" toast body with detailed reasons. */
export function buildUnsupportedTypeMessage(rejected: File[], preset: FileTypePreset): string {
  const reasons = rejected.map((f) => getFileRejectionReason(f, preset)).join('; ');
  return `Allowed types: ${preset.label}. ${reasons}`;
}
